/**
 * Datas na língua da casa, e a conta do "juntos há".
 *
 * O `Intl` já sabe os dois idiomas; trazer uma biblioteca de datas para
 * formatar meia dúzia de coisas seria pagar peso por algo que vem de graça.
 *
 * Os formatadores nascem sob demanda e ficam guardados por idioma. Antes eram
 * constantes de módulo, mais simples — mas uma constante de módulo é construída
 * uma vez e nunca mais, e quem troca de idioma no meio da sessão continuaria
 * vendo "31 de dezembro" numa tela em inglês. Construir um `Intl.DateTimeFormat`
 * é caro; procurá-lo num `Map` não é.
 *
 * O que o `Intl` não resolve sozinho está escrito à mão aqui embaixo, e sempre
 * pelo mesmo motivo: o português tem de sair igual ao do Flutter, letra por
 * letra, e o `Intl` pt-BR escreve "dez." com ponto e "setembro" com minúscula.
 * Nesses três lugares o português é lista fixa e o inglês é `Intl`.
 */

import { idioma, locale } from '../i18n/idioma';

const guardados = new Map<string, Intl.DateTimeFormat>();

function fmt(nome: string, opcoes: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const etiqueta = locale();
  const chave = `${etiqueta}:${nome}`;
  let f = guardados.get(chave);
  if (!f) guardados.set(chave, (f = new Intl.DateTimeFormat(etiqueta, opcoes)));
  return f;
}

/**
 * O `MMMM d, yyyy` do cartão do arquivo vivo — em inglês, sempre, mesmo com o
 * app em português.
 *
 * Não é escolha de estilo nem descuido de tradução: o Flutter nunca chamou
 * `initializeDateFormatting` (está anotado em
 * `lib/features/menu/data/models/week_menu.dart`), então o `intl` cai no `en_US`
 * e é "September 9, 2026" que o outro app mostra. Traduzir aqui faria as duas
 * telas discordarem sobre a mesma data. É o único formatador que não acompanha
 * o idioma, e é de propósito.
 */
const AMERICANA = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});

export const porExtenso = (d: Date): string =>
  fmt('porExtenso', { day: '2-digit', month: 'long', year: 'numeric' }).format(d);

export const curto = (d: Date): string =>
  fmt('curto', { day: '2-digit', month: 'short' }).format(d);

export const hora = (d: Date): string =>
  fmt('hora', { hour: '2-digit', minute: '2-digit' }).format(d);

/** O `d/MM/yyyy` do Flutter em português: dia sem zero à frente, mês com. */
export const numerica = (d: Date): string =>
  fmt('numerica', { day: 'numeric', month: '2-digit', year: 'numeric' }).format(d);

export const americana = (d: Date): string => AMERICANA.format(d);

/**
 * O que o Flutter grava: ISO em UTC. Devolve `null` no que não for data.
 *
 * Aceita também milissegundos em número, como o `dataDe` do Flutter: documentos
 * antigos gravaram assim, e sem isto a data deles some da tela sem erro nenhum.
 */
export function lerData(valor: unknown): Date | null {
  if (typeof valor === 'number') {
    return Number.isFinite(valor) ? new Date(valor) : null;
  }
  if (typeof valor !== 'string' || !valor) return null;
  const d = new Date(valor);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** O formato que o Flutter grava, para os dois apps lerem o mesmo campo. */
export const gravarData = (d: Date = new Date()): string => d.toISOString();

/** `MM-DD`, do jeito que o `monthDay` do Flutter é montado. */
export function mesDia(d: Date = new Date()): string {
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const MES_CURTO = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];

/** `31 de dez de 2021` em português, igual ao `startedOnShort` do Flutter. */
export function comecoCurto(d: Date): string {
  if (idioma() !== 'pt') {
    return fmt('comecoCurto', { month: 'short', day: 'numeric', year: 'numeric' }).format(d);
  }
  return `${d.getDate()} de ${MES_CURTO[d.getMonth()]} de ${d.getFullYear()}`;
}

const MES_LONGO = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

/** `Setembro 2026`, o cabeçalho do mês no calendário. */
export function mesEAno(d: Date): string {
  if (idioma() !== 'pt') return fmt('mesEAno', { month: 'long', year: 'numeric' }).format(d);
  return `${MES_LONGO[d.getMonth()]} ${d.getFullYear()}`;
}

/** `5/09` em português, o `DateFormat('d/MM')` das datas curtas do calendário. */
export function diaEMes(d: Date): string {
  if (idioma() !== 'pt') return fmt('diaEMes', { day: 'numeric', month: 'numeric' }).format(d);
  return `${d.getDate()}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const SEMANA_PT = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
const SEMANA_PT_CURTA = ['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB', 'DOM'];

/**
 * Uma segunda-feira qualquer, e os seis dias depois dela: é o que o `Intl`
 * precisa para dizer o nome de cada dia. 6 de janeiro de 2025 caiu numa segunda.
 * Ao meio-dia para nenhum fuso do mundo empurrar a data para o dia anterior.
 */
const UMA_SEMANA = Array.from({ length: 7 }, (_, n) => new Date(2025, 0, 6 + n, 12));

/**
 * A semana começando na segunda, que é como o calendário do app a desenha.
 *
 * `getDay()` conta do domingo, então quem indexa estas duas listas precisa do
 * `(dia + 6) % 7` — está no `posicaoNaSemana` logo abaixo.
 *
 * São funções, e não constantes, porque o nome do dia muda com a língua. Em
 * português a lista é fixa: o `Intl` pt-BR diria "segunda-feira", e o app
 * sempre escreveu "Segunda".
 */
export function diasDaSemana(): string[] {
  if (idioma() === 'pt') return SEMANA_PT;
  const f = fmt('diaDaSemana', { weekday: 'long' });
  return UMA_SEMANA.map((d) => f.format(d));
}

export function diasDaSemanaCurtos(): string[] {
  if (idioma() === 'pt') return SEMANA_PT_CURTA;
  const f = fmt('diaDaSemanaCurto', { weekday: 'short' });
  return UMA_SEMANA.map((d) => f.format(d).toUpperCase());
}

/** Onde o dia cai numa semana que começa na segunda: 0 é segunda, 6 é domingo. */
export const posicaoNaSemana = (d: Date): number => (d.getDay() + 6) % 7;

export interface Juntos {
  anos: number;
  meses: number;
  dias: number;
  horas: number;
  minutos: number;
  segundos: number;
  totalDias: number;
}

/**
 * Quanto tempo desde o começo, em anos, meses e dias de calendário.
 *
 * Contar só em dias daria um número certo e sem graça; contar em meses de 30
 * daria um número errado. Isto empresta do mês anterior quando o dia ainda não
 * chegou, que é como as pessoas contam.
 *
 * Hora, minuto e segundo saem do tempo corrido (`% 24`, `% 60`, `% 60`), sem
 * olhar o empréstimo do calendário — é assim que o `AnniversaryCounter` do
 * Flutter faz, e os dois contadores precisam bater no mesmo segundo.
 */
export function juntosDesde(inicio: Date, agora: Date = new Date()): Juntos {
  if (agora < inicio) {
    return { anos: 0, meses: 0, dias: 0, horas: 0, minutos: 0, segundos: 0, totalDias: 0 };
  }

  let anos = agora.getFullYear() - inicio.getFullYear();
  let meses = agora.getMonth() - inicio.getMonth();
  let dias = agora.getDate() - inicio.getDate();

  if (dias < 0) {
    meses -= 1;
    // O último dia do mês anterior ao de agora: é dele que se empresta.
    dias += new Date(agora.getFullYear(), agora.getMonth(), 0).getDate();
  }
  if (meses < 0) {
    anos -= 1;
    meses += 12;
  }

  const corrido = agora.getTime() - inicio.getTime();
  const totalDias = Math.floor(corrido / 86_400_000);
  const horas = Math.floor(corrido / 3_600_000) % 24;
  const minutos = Math.floor(corrido / 60_000) % 60;
  const segundos = Math.floor(corrido / 1_000) % 60;
  return { anos, meses, dias, horas, minutos, segundos, totalDias };
}

/**
 * "2 anos, 3 meses e 4 dias".
 *
 * O plural está escrito aqui e não no dicionário de `palavras.ts` porque são
 * seis palavras soltas — "ano", "dia", "mês" — que sozinhas querem dizer coisa
 * demais para virarem verbete. O `Intl.ListFormat` faria a emenda final sozinho
 * e nos dois idiomas, mas o Hermes do celular não o traz: a emenda é à mão para
 * as duas telas dizerem a mesma frase.
 */
export function juntosEmPalavras(j: Juntos): string {
  const en = idioma() !== 'pt';
  const conta = (n: number, pt: [string, string], ingles: [string, string]) => {
    const par = en ? ingles : pt;
    return `${n} ${n === 1 ? par[0] : par[1]}`;
  };

  const partes: string[] = [];
  if (j.anos) partes.push(conta(j.anos, ['ano', 'anos'], ['year', 'years']));
  if (j.meses) partes.push(conta(j.meses, ['mês', 'meses'], ['month', 'months']));
  if (j.dias || partes.length === 0) partes.push(conta(j.dias, ['dia', 'dias'], ['day', 'days']));
  if (partes.length === 1) return partes[0];
  return `${partes.slice(0, -1).join(', ')}${en ? ' and ' : ' e '}${partes[partes.length - 1]}`;
}
